import { useCallback } from 'react'
import {
  useCreateIntegrationMutation,
  useCreateSecretMutation,
  useDeleteIntegrationMutation,
  useDeleteSecretMutation,
  useLazyListSecretsQuery,
  useUpdateIntegrationMutation,
  useUpdateSecretMutation,
  type Integration,
  type IntegrationService,
  type Secret,
  type UpdateIntegrationPayload
} from '@axilon-apps/domains-ontology-ui-state'

import { buildSecretKey } from './secret-name'

export interface CreateIntegrationInput {
  service: IntegrationService
  instanceName: string
  /** Plaintext API key. Empty string means "no credential" (no secret created). */
  apiKey: string
  /** Federate into technician work orders (ServiceNow). Applied via PATCH after create. */
  workOrdersEnabled?: boolean
}

export interface UpdateIntegrationInput {
  integration: Integration
  instanceName: string
  /** When non-empty, rotate (or attach) the credential to this new value. */
  newApiKey?: string
  /** When set, PATCH `work_orders_enabled` (ServiceNow federation opt-in). */
  workOrdersEnabled?: boolean
}

function secretDescription(service: IntegrationService, instanceName: string): string {
  return `API key for ${service} integration "${instanceName}"`
}

/**
 * Orchestrates the two-API credential flows behind the Integrations tab.
 * The proxy stores secret material (Secrets API) separately from the
 * integration record (Integrations API), and the integration only ever
 * references a secret by logical name (`api_key_name`), never by value.
 *
 * Create flow (per product spec):
 *   1. Write the API key as a new secret.
 *   2. Create the integration referencing that secret by name.
 *   3. If step 2 fails, delete the orphaned secret (rollback).
 *
 * Update flow:
 *   • If a new key is supplied, rotate the existing secret in place
 *     (value-only PATCH, key/name preserved) or create+attach one when
 *     the integration had none / its secret has gone missing.
 *   • Patch the integration. If THIS fails we intentionally do NOT delete
 *     the secret — the user may simply retry the metadata update.
 */
export function useIntegrationCredentialFlows() {
  const [createSecret, createSecretState] = useCreateSecretMutation()
  const [updateSecret, updateSecretState] = useUpdateSecretMutation()
  const [deleteSecret] = useDeleteSecretMutation()
  const [listSecrets] = useLazyListSecretsQuery()
  const [createIntegration, createIntegrationState] = useCreateIntegrationMutation()
  const [updateIntegration, updateIntegrationState] = useUpdateIntegrationMutation()
  const [deleteIntegration, deleteIntegrationState] = useDeleteIntegrationMutation()

  const findSecretByKey = useCallback(
    async (key: string): Promise<Secret | undefined> => {
      // `q` is a case-insensitive substring filter, so confirm an exact
      // key match client-side.
      const secrets = await listSecrets({ q: key, page_size: 500 }).unwrap()
      return secrets.find((s) => s.key === key)
    },
    [listSecrets]
  )

  const createIntegrationWithSecret = useCallback(
    async ({
      service,
      instanceName,
      apiKey,
      workOrdersEnabled
    }: CreateIntegrationInput): Promise<Integration> => {
      const trimmedKey = apiKey.trim()
      const enableWorkOrders = Boolean(workOrdersEnabled)

      const applyWorkOrdersFlag = async (created: Integration): Promise<Integration> => {
        if (!enableWorkOrders) return created
        return updateIntegration({
          id: created.id,
          patch: {
            work_orders_enabled: true,
            ...(service === 'servicenow' ? { config: { table: 'incident' } } : {})
          }
        }).unwrap()
      }

      // No credential → just create the integration with no secret.
      if (!trimmedKey) {
        const created = await createIntegration({
          service,
          instance_name: instanceName
        }).unwrap()
        return applyWorkOrdersFlag(created)
      }

      const key = buildSecretKey(service, instanceName)
      const secret = await createSecret({
        key,
        value: trimmedKey,
        description: secretDescription(service, instanceName)
      }).unwrap()

      try {
        const created = await createIntegration({
          service,
          instance_name: instanceName,
          api_key_name: secret.key
        }).unwrap()
        return await applyWorkOrdersFlag(created)
      } catch (err) {
        // Roll back the now-orphaned secret. Swallow rollback errors so
        // the user sees the original integration-creation failure.
        try {
          await deleteSecret(secret.id).unwrap()
        } catch {
          /* best-effort cleanup */
        }
        throw err
      }
    },
    [createIntegration, createSecret, deleteSecret, updateIntegration]
  )

  const updateIntegrationWithSecret = useCallback(
    async ({
      integration,
      instanceName,
      newApiKey,
      workOrdersEnabled
    }: UpdateIntegrationInput): Promise<Integration> => {
      const trimmedKey = (newApiKey ?? '').trim()
      let apiKeyName = integration.api_key_name

      if (trimmedKey) {
        const existing = integration.api_key_name
          ? await findSecretByKey(integration.api_key_name)
          : undefined

        if (existing) {
          // Rotate in place: value-only PATCH keeps the same logical name.
          await updateSecret({ id: existing.id, patch: { value: trimmedKey } }).unwrap()
        } else {
          // Either the integration had no secret, or its referenced
          // secret is gone — create a fresh one and re-point the
          // integration at it.
          const created = await createSecret({
            key: buildSecretKey(integration.service, instanceName),
            value: trimmedKey,
            description: secretDescription(integration.service, instanceName)
          }).unwrap()
          apiKeyName = created.key
        }
      }

      const patch: UpdateIntegrationPayload = { instance_name: instanceName }
      if (apiKeyName !== integration.api_key_name) {
        patch.api_key_name = apiKeyName
      }
      if (workOrdersEnabled !== undefined) {
        const enabled = Boolean(workOrdersEnabled)
        patch.work_orders_enabled = enabled
        const existingTable = integration.config?.['table']
        if (
          enabled &&
          integration.service === 'servicenow' &&
          (typeof existingTable !== 'string' || existingTable.length === 0)
        ) {
          patch.config = { table: 'incident' }
        }
      }

      // No secret rollback on failure here — spec: keep the secret.
      return updateIntegration({ id: integration.id, patch }).unwrap()
    },
    [createSecret, findSecretByKey, updateIntegration, updateSecret]
  )

  const deleteIntegrationWithSecret = useCallback(
    async (integration: Integration): Promise<void> => {
      await deleteIntegration(integration.id).unwrap()
      // Best-effort cleanup of the integration-owned secret. Each
      // integration owns a uniquely-named secret, so this never removes a
      // shared credential. Failures (incl. a 404 if it is already gone)
      // are swallowed: the integration is already deleted.
      if (integration.api_key_name) {
        try {
          const existing = await findSecretByKey(integration.api_key_name)
          if (existing) await deleteSecret(existing.id).unwrap()
        } catch {
          /* best-effort cleanup */
        }
      }
    },
    [deleteIntegration, deleteSecret, findSecretByKey]
  )

  const isCreating = createSecretState.isLoading || createIntegrationState.isLoading
  const isUpdating =
    updateSecretState.isLoading || createSecretState.isLoading || updateIntegrationState.isLoading
  const isDeleting = deleteIntegrationState.isLoading

  return {
    createIntegrationWithSecret,
    updateIntegrationWithSecret,
    deleteIntegrationWithSecret,
    isCreating,
    isUpdating,
    isDeleting
  }
}
